import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Card, CardContent, Typography, Button } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import TimerIcon from '@mui/icons-material/Timer';
import WorkIcon from '@mui/icons-material/Work';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import FolderIcon from '@mui/icons-material/Folder';
import HHHubLogo from './assets/images/HHG-Hub-Logo-Color-RGB.png';

const gradientAnimation = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
`;

const iconFloat = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-5px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const floatAnimation = keyframes`
  0%, 100% {
    transform: translateY(0) translateX(0);
  }
  25% {
    transform: translateY(-15px) translateX(15px);
  }
  50% {
    transform: translateY(-25px) translateX(0);
  }
  75% {
    transform: translateY(-15px) translateX(-15px);
  }
`;

const rotateAnimation = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

const lineAnimation = keyframes`
  0% {
    opacity: 0;
    transform: translateX(-100%);
  }
  50% {
    opacity: 0.5;
  }
  100% {
    opacity: 0;
    transform: translateX(100%);
  }
`;

const pulseAnimation = keyframes`
  0% {
    opacity: 0.1;
  }
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 0.1;
  }
`;

const AppContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(-45deg, #dceafe, #ffffff, #c5e1fd, #e8f3ff);
  background-size: 400% 400%;
  animation: ${gradientAnimation} 20s ease-in-out infinite;
  padding: clamp(1rem, 5vw, 2rem);
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;
  z-index: 1;
`;

const Header = styled.header`
  text-align: center;
  color: #0e2240;
  margin-bottom: clamp(2rem, 5vw, 4rem);
  margin-top: clamp(2rem, 5vw, 3rem);
  opacity: 0;
  animation: fadeIn 0.8s ease forwards;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const Logo = styled.img`
  height: auto;
  width: 100%;
  max-width: 300px;
  margin: 0 auto;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    max-width: 70%;
  }
`;

const Description = styled.div`
  color: #527a97;
  font-size: clamp(1rem, 2.5vw, 1.125rem);
  white-space: nowrap;
  overflow-x: auto;
  max-width: 100%;
  margin: 1rem auto;
  text-align: center;
  padding: 0 1rem;
  
  @media (max-width: 768px) {
    white-space: normal;
    overflow-x: visible;
  }
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1rem;
  
  @media (max-width: 1200px) {
    gap: 1.5rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const CardHeader = styled.div`
  background: linear-gradient(135deg, #0e2240 0%, #527a97 100%);
  padding: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  overflow: hidden;
  border-radius: 24px 24px 0 0;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 70%);
    opacity: 0.7;
    transition: opacity 0.3s ease;
  }
`;

const StyledCard = styled(Card)`
  transition: all 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
  border-radius: 24px !important;
  box-shadow: 0 4px 20px rgba(14, 34, 64, 0.08);
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  overflow: hidden;
  opacity: 0;
  animation: fadeInUp 0.6s cubic-bezier(0.165, 0.84, 0.44, 1) forwards;
  animation-delay: calc(0.1s * var(--index));
  
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &:hover {
    transform: translateY(-8px) scale(1.02);
    box-shadow: 0 12px 40px rgba(14, 34, 64, 0.12);
    
    ${CardHeader} {
      &::after {
        opacity: 1;
      }
    }
  }

  .MuiPaper-root {
    border-radius: 24px;
  }

  .MuiCard-root {
    border-radius: 24px;
  }

  .MuiCardContent-root {
    border-radius: 0 0 24px 24px;
  }

  @media (max-width: 768px) {
    transform: none !important;
    
    &:hover {
      transform: translateY(-4px) !important;
    }
  }
`;

const StyledButton = styled(Button)`
  &.MuiButton-root {
    padding: 12px 28px;
    text-transform: none;
    font-weight: 600;
    border-width: 2px;
    border-radius: 12px;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    background: transparent;
    overflow: hidden;
    letter-spacing: 0.3px;
    font-size: 1.125rem;
    
    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: #0e2240;
      opacity: 0;
      transition: opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    }
    
    &:hover {
      transform: translateY(-2px);
      border-color: #0e2240;
      box-shadow: 0 6px 20px rgba(14, 34, 64, 0.12);
      
      &::before {
        opacity: 0.05;
      }
      
      .MuiButton-label {
        color: #0e2240;
      }
    }
    
    &:active {
      transform: translateY(0);
      box-shadow: 0 2px 10px rgba(14, 34, 64, 0.08);
    }
    
    .MuiButton-label {
      position: relative;
      z-index: 1;
    }
  }
`;

const NoticeLabel = styled.div`
  background: rgba(14, 34, 64, 0.08);
  color: #0e2240;
  padding: 8px 16px;
  border-radius: 12px;
  font-size: 0.95rem;
  font-weight: 600;
  margin: 4px 0;
  letter-spacing: 0.5px;
  border: 1px solid rgba(14, 34, 64, 0.12);
  box-shadow: 0 2px 8px rgba(14, 34, 64, 0.05);
  display: inline-block;
  position: relative;
  
  &::before {
    content: '⚠️';
    margin-right: 6px;
    font-size: 1rem;
  }
`;

const NewBanner = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, #0e2240 0%, #527a97 100%);
  color: white;
  padding: 6px 16px;
  border-radius: 12px;
  font-size: 0.875rem;
  font-weight: 600;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(14, 34, 64, 0.15);
  z-index: 2;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const IconWrapper = styled.div`
  background: rgba(255, 255, 255, 0.1);
  padding: clamp(15px, 4vw, 20px);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  z-index: 1;
  animation: ${iconFloat} 3s ease-in-out infinite;
  
  svg {
    font-size: clamp(30px, 5vw, 40px) !important;
  }
`;

const BackgroundElements = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
  pointer-events: none;
  z-index: 0;
`;

const Circle = styled.div<{ size: number; top: number; left: number; delay: number }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  border-radius: 50%;
  border: 2px solid rgba(82, 122, 151, 0.1);
  animation: ${floatAnimation} 15s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
`;

const Square = styled.div<{ size: number; top: number; left: number; delay: number }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  border: 2px solid rgba(14, 34, 64, 0.08);
  animation: ${rotateAnimation} 20s linear infinite;
  animation-delay: ${props => props.delay}s;
`;

const Dot = styled.div<{ size: number; top: number; left: number; delay: number }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  top: ${props => props.top}%;
  left: ${props => props.left}%;
  background: rgba(82, 122, 151, 0.1);
  border-radius: 50%;
  animation: ${floatAnimation} 12s ease-in-out infinite;
  animation-delay: ${props => props.delay}s;
`;

const TechPattern = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  overflow: hidden;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: 
      linear-gradient(90deg, transparent 0%, transparent 49%, rgba(82, 122, 151, 0.08) 50%, transparent 51%, transparent 100%),
      linear-gradient(0deg, transparent 0%, transparent 49%, rgba(82, 122, 151, 0.08) 50%, transparent 51%, transparent 100%);
    background-size: 30px 30px;
    opacity: 1;
    animation: ${pulseAnimation} 4s ease-in-out infinite;
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 200%;
    height: 100%;
    background: linear-gradient(90deg, 
      transparent 0%,
      rgba(14, 34, 64, 0.1) 25%,
      rgba(82, 122, 151, 0.15) 50%,
      rgba(14, 34, 64, 0.1) 75%,
      transparent 100%
    );
    animation: ${lineAnimation} 6s linear infinite;
  }
`;

const CircuitLine = styled.div<{ top: number; delay: number; direction: 'horizontal' | 'vertical' }>`
  position: absolute;
  ${props => props.direction === 'horizontal' ? `
    left: 0;
    right: 0;
    height: 2px;
    top: ${props.top}%;
  ` : `
    top: 0;
    bottom: 0;
    width: 2px;
    left: ${props.top}%;
  `}
  background: linear-gradient(${props => props.direction === 'horizontal' ? '90deg' : '0deg'}, 
    transparent 0%,
    rgba(82, 122, 151, 0.2) 50%,
    transparent 100%
  );
  opacity: 0;
  animation: ${lineAnimation} 8s linear infinite;
  animation-delay: ${props => props.delay}s;
  box-shadow: 0 0 8px rgba(82, 122, 151, 0.1);
`;

const App: React.FC = () => {
  const circuitLines: Array<{
    top: number;
    delay: number;
    direction: 'horizontal' | 'vertical';
  }> = [
    { top: 15, delay: 0, direction: 'horizontal' },
    { top: 35, delay: 2, direction: 'horizontal' },
    { top: 55, delay: 4, direction: 'horizontal' },
    { top: 75, delay: 6, direction: 'horizontal' },
    { top: 95, delay: 8, direction: 'horizontal' },
    { top: 20, delay: 1, direction: 'vertical' },
    { top: 40, delay: 3, direction: 'vertical' },
    { top: 60, delay: 5, direction: 'vertical' },
    { top: 80, delay: 7, direction: 'vertical' }
  ];

  const services = [
    {
      title: 'H+H Hub',
      icon: <FolderIcon sx={{ fontSize: 40 }} />,
      description: 'Access H+H Hub resources and documents',
      url: 'https://hhck.sharepoint.com/',
      notice: ''
    },
    {
      title: 'ADP',
      icon: <AccountBalanceIcon sx={{ fontSize: 40 }} />,
      description: 'Time and attendance management system',
      url: 'https://online.adp.com/signin/v1/?APPID=WFNPortal&productId=80e309c3-7085-bae1-e053-3505430b5495&returnURL=https://workforcenow.adp.com/&callingAppId=WFN&TARGET=-SM-https://workforcenow.adp.com/theme/index.html',
      notice: ''
    }
  ];

  return (
    <>
      <TechPattern>
        {circuitLines.map((line, index) => (
          <CircuitLine
            key={index}
            top={line.top}
            delay={line.delay}
            direction={line.direction}
          />
        ))}
      </TechPattern>
      <BackgroundElements>
        <Circle size={100} top={15} left={10} delay={0} />
        <Circle size={150} top={75} left={85} delay={2} />
        <Circle size={80} top={45} left={70} delay={1} />
        <Square size={120} top={25} left={80} delay={0} />
        <Square size={90} top={65} left={15} delay={2} />
        <Square size={60} top={85} left={45} delay={1} />
        <Dot size={10} top={20} left={30} delay={0} />
        <Dot size={15} top={70} left={60} delay={1} />
        <Dot size={12} top={40} left={85} delay={2} />
        <Dot size={8} top={90} left={25} delay={1.5} />
      </BackgroundElements>
      <AppContainer>
        <Header>
          <Logo src={HHHubLogo} alt="H+H Hub Logo" />
          <Description>
            H+H HUB is your central location for all things H+H including, request forms, Kudos, ClickUp, and much more.
          </Description>
        </Header>

        <CardsContainer>
          {services.map((service, index) => (
            <StyledCard key={index} style={{ '--index': index } as React.CSSProperties}>
              <CardHeader>
                <IconWrapper>
                  {service.icon}
                </IconWrapper>
              </CardHeader>
              <CardContent sx={{ 
                textAlign: 'center', 
                padding: 'clamp(20px, 4vw, 28px)',
                display: 'flex',
                flexDirection: 'column',
                borderRadius: '0 0 24px 24px',
                position: 'relative',
                paddingBottom: 'clamp(60px, 8vw, 80px)',
                minHeight: 'clamp(220px, 30vw, 250px)'
              }}>
                <div>
                  <Typography 
                    variant="h6" 
                    component="h2" 
                    sx={{ 
                      fontWeight: '600',
                      color: '#0e2240',
                      letterSpacing: '0.5px',
                      marginBottom: '16px',
                      fontSize: 'clamp(1.125rem, 3vw, 1.375rem)'
                    }}
                  >
                    {service.title}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#527a97',
                      lineHeight: 1.6,
                      marginBottom: service.notice && service.notice.length > 0 ? '16px' : '0',
                      fontSize: 'clamp(1rem, 2.5vw, 1.125rem)'
                    }}
                  >
                    {service.description}
                  </Typography>
                  {service.notice && service.notice.length > 0 && (
                    <NoticeLabel>
                      {service.notice}
                    </NoticeLabel>
                  )}
                </div>
                <div style={{
                  position: 'absolute',
                  bottom: '28px',
                  left: '28px',
                  right: '28px',
                  display: 'flex',
                  justifyContent: 'center'
                }}>
                  <StyledButton
                    variant="outlined"
                    href={service.url}
                    fullWidth
                    sx={{
                      color: '#0e2240',
                      borderColor: '#0e2240',
                      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
                      '&:hover': {
                        borderColor: '#0e2240',
                      }
                    }}
                  >
                    Visit Site
                  </StyledButton>
                </div>
              </CardContent>
            </StyledCard>
          ))}
        </CardsContainer>
      </AppContainer>
    </>
  );
};

export default App;
